// Guardas de acceso del Panel Cliente. Igual que en panel-super-admin
// (ver su guardas.js), son una capa de EXPERIENCIA, no la capa de
// seguridad real — esa es RLS (20260101000006_seguridad_rls.sql), que
// rechaza cualquier consulta igual si alguien se salta esto editando el JS
// del navegador.
//
// A diferencia de super-admin, acá hay DOS roles válidos (admin_empresa,
// editor_empresa) y algunas vistas son exclusivas de admin_empresa (ej.
// gestión de datos de facturación/plan, si se agrega más adelante) — de ahí
// la guarda extra `exigirAdminEmpresa`, hoy sin uso porque el alcance MVP
// del Panel Cliente no tiene todavía un módulo admin-only, pero se deja
// lista para cuando aparezca (patrón ya usado por RLS mismo, que en
// 20260101000006 sí distingue admin_empresa de editor_empresa para el
// módulo de usuarios internos — ver esa migración).
//
// Autenticación: el login de usuarios existentes vive en /login del panel.
// Si no hay sesión, se redirige a /login.

import { navegarA } from './enrutador.js';
import { obtenerEstadoSesion } from './estado-sesion.js';

const ROLES_CON_ACCESO = ['admin_empresa', 'editor_empresa'];
const RUTAS_LIBRES = new Set(['/login', '/sin-permiso']);
const RUTAS_RECUPERACION = new Set(['/cambiar-contrasena']);
const RUTAS_REGULARIZACION = new Set(['/pagos']);
const ESTADOS_REGULARIZACION = ['prueba_terminada', 'vencida'];
const ESTADOS_SIN_OPERACION = ['suspendida', 'dada_de_baja'];

export function empresaRequiereRegularizacion(empresa) {
  return ESTADOS_REGULARIZACION.includes(empresa?.estado);
}

/**
 * Guarda central para el router. Evita que una empresa con cuenta vencida
 * monte vistas operativas escribiendo el hash manualmente.
 *
 * @param {string} ruta
 * @returns {string|null}
 */
export function resolverRedireccionRutaEmpresa(ruta) {
  const { sesion, claims, empresa } = obtenerEstadoSesion();

  if (RUTAS_LIBRES.has(ruta)) return null;

  if (!sesion) {
    return RUTAS_RECUPERACION.has(ruta) ? null : '/login';
  }

  if (!ROLES_CON_ACCESO.includes(claims.rol) || !claims.empresa_id) {
    return ruta === '/sin-permiso' ? null : '/sin-permiso';
  }

  if (!empresa) return null;

  if (empresaRequiereRegularizacion(empresa) && !RUTAS_REGULARIZACION.has(ruta)) {
    return '/pagos';
  }

  if (ESTADOS_SIN_OPERACION.includes(empresa.estado) && ruta !== '/empresa-suspendida') {
    return '/empresa-suspendida';
  }

  return null;
}

/**
 * Verifica que haya una sesión activa con rol `admin_empresa` o
 * `editor_empresa` y con `empresa_id` presente en el JWT. Si no, redirige a
 * /login y devuelve `false`.
 *
 * @returns {boolean}
 */
export function exigirSesionEmpresa() {
  const { sesion, claims } = obtenerEstadoSesion();
  const rutaActual =
    typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') || '/' : '/';

  if (!sesion) {
    if (rutaActual !== '/login') {
      navegarA('/login');
    }
    return false;
  }

  if (!ROLES_CON_ACCESO.includes(claims.rol) || !claims.empresa_id) {
    navegarA('/sin-permiso');
    return false;
  }

  return true;
}

/**
 * Verifica que la empresa no esté en un estado que impida operar.
 * La restricción por prueba_terminada/vencida vive en la guarda central del
 * router para que aplique a todas las rutas, no solo a las vistas que llamen
 * esta función.
 *
 * @param {{ estado?: string }} empresa
 * @returns {boolean}
 */
export function exigirEmpresaActiva(empresa) {
  if (empresa?.estado === 'suspendida' || empresa?.estado === 'dada_de_baja') {
    navegarA('/empresa-suspendida');
    return false;
  }

  return true;
}

/**
 * Determina si la empresa tiene acceso restringido. Se conserva para vistas
 * que necesiten mensajes específicos; la navegación se decide centralmente.
 *
 * @param {{ estado?: string }} empresa
 * @returns {boolean}
 */
export function empresaEnModoRestringido(empresa) {
  return empresa?.estado === 'vencida';
}

/**
 * Como `exigirSesionEmpresa`, pero exige además el rol `admin_empresa`
 * específicamente (excluye `editor_empresa`).
 *
 * @returns {boolean}
 */
export function exigirAdminEmpresa() {
  if (!exigirSesionEmpresa()) return false;

  const { claims } = obtenerEstadoSesion();
  if (claims.rol !== 'admin_empresa') {
    navegarA('/sin-permiso');
    return false;
  }

  return true;
}
